import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

// Tarih aralığı seçici — preset listesi + takvim. SQLiteHistory ve LpTrax ortak kullanır.

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const DAYS_TR   = ['Pz','Pt','Sa','Ça','Pe','Cu','Ct'];

export function toDStr(d: Date) {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function getPresets() {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const yest  = new Date(now); yest.setDate(yest.getDate() - 1);
  const lw    = new Date(now); lw.setDate(lw.getDate() - 7);
  const lm    = new Date(now); lm.setMonth(lm.getMonth() - 1);
  const l3m   = new Date(now); l3m.setMonth(l3m.getMonth() - 3);
  const ytd   = new Date(now.getFullYear(), 0, 1);
  const lyS   = new Date(now.getFullYear() - 1, 0, 1);
  const lyE   = new Date(now.getFullYear() - 1, 11, 31);
  return [
    { label: 'Bugün',         from: toDStr(now),  to: toDStr(now)  },
    { label: 'Dün',           from: toDStr(yest), to: toDStr(yest) },
    { label: 'Son 1 Hafta',   from: toDStr(lw),   to: toDStr(now)  },
    { label: 'Son 1 Ay',      from: toDStr(lm),   to: toDStr(now)  },
    { label: 'Son 3 Ay',      from: toDStr(l3m),  to: toDStr(now)  },
    { label: 'Yıl Başından',  from: toDStr(ytd),  to: toDStr(now)  },
    { label: 'Geçen Yıl',     from: toDStr(lyS),  to: toDStr(lyE)  },
    { label: 'Tümü',          from: '',           to: ''           },
  ];
}

export function fmtRangeLabel(from: string, to: string) {
  if (!from && !to) return 'Tümü';
  const fmtD = (s: string) => {
    const d = new Date(s);
    return `${d.getDate()} ${MONTHS_TR[d.getMonth()].slice(0,3)}`;
  };
  if (from === to) return fmtD(from);
  if (!to)   return `${fmtD(from)} →`;
  return `${fmtD(from)} – ${fmtD(to)}`;
}

/** "YYYY-MM-DD" aralığı → [fromMs, toMs] (toMs gün sonu). Boş string → 0 (sınırsız). */
export function rangeToMs(from: string, to: string): [number, number] {
  return [
    from ? new Date(from + 'T00:00:00').getTime() : 0,
    to   ? new Date(to   + 'T00:00:00').getTime() + 86_400_000 - 1 : 0,
  ];
}

interface DateRangePickerProps {
  dateFrom: string; dateTo: string;
  onApply: (from: string, to: string) => void;
  dark?: boolean;
}

export default function DateRangePicker({ dateFrom, dateTo, onApply, dark = true }: DateRangePickerProps) {
  const [open,    setOpen]    = useState(false);
  const [pFrom,   setPFrom]   = useState('');
  const [pTo,     setPTo]     = useState('');
  const [vy,      setVy]      = useState(() => new Date().getFullYear());
  const [vm,      setVm]      = useState(() => new Date().getMonth());
  const ref = useRef<HTMLDivElement>(null);
  const todayStr = toDStr(new Date());
  const presets  = getPresets();

  // Tema yüzeyleri (varsayılan koyu — SQLiteHistory'deki mevcut görünüm)
  const popBg   = dark ? 'border-[#0a2560] bg-[#0d0a1f]' : 'border-[#d4e0f0] bg-white';
  const divide  = dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]';
  const hov     = dark ? 'hover:bg-[#1a0e38] hover:text-white' : 'hover:bg-[#eef3fa] hover:text-slate-900';
  const hovBtn  = dark ? 'hover:bg-[#1e1040]' : 'hover:bg-[#eef3fa]';
  const presetActive = dark ? 'text-white bg-[#1e1040]' : 'text-slate-900 bg-[#dceafc]';
  const presetIdle   = dark ? 'text-blue-300' : 'text-slate-600';
  const dayIdle = dark ? 'text-blue-300' : 'text-slate-600';
  const titleC  = dark ? 'text-white' : 'text-slate-900';
  const cancelC = dark ? 'border-blue-700/60 text-blue-300' : 'border-[#d4e0f0] text-slate-600';

  // Dışarı tıklandığında kapat
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setPFrom(dateFrom);
    setPTo(dateTo);
    const base = dateFrom ? new Date(dateFrom) : new Date();
    setVy(base.getFullYear());
    setVm(base.getMonth());
    setOpen(true);
  };

  const handlePreset = (from: string, to: string) => {
    onApply(from, to);
    setOpen(false);
  };

  const handleDayClick = (d: string) => {
    if (!pFrom || (pFrom && pTo)) {
      setPFrom(d); setPTo('');
    } else {
      if (d >= pFrom) setPTo(d);
      else { setPTo(pFrom); setPFrom(d); }
    }
  };

  const prevMonth = () => { if (vm === 0) { setVm(11); setVy(y => y - 1); } else setVm(m => m - 1); };
  const nextMonth = () => { if (vm === 11) { setVm(0); setVy(y => y + 1); } else setVm(m => m + 1); };

  // Takvim hücreleri
  const daysInMonth  = new Date(vy, vm + 1, 0).getDate();
  const firstDow     = new Date(vy, vm, 1).getDay();
  const cells: (string | null)[] = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => {
      const d = i + 1;
      return `${vy}-${String(vm + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    }),
  ];

  const isFrom    = (d: string) => d === pFrom;
  const isTo      = (d: string) => d === pTo;
  const inRange   = (d: string) => pFrom && pTo && d > pFrom && d < pTo;
  const isToday   = (d: string) => d === todayStr;

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={handleOpen}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors ${
          dateFrom || dateTo
            ? 'border-[#4A9FE8]/70 text-[#4A9FE8] bg-[#4A9FE8]/10'
            : dark
              ? 'border-blue-700/60 text-blue-300 hover:border-blue-500/60 hover:text-blue-200'
              : 'border-[#d4e0f0] text-slate-600 hover:border-[#4A9FE8]/60 hover:text-slate-900'
        }`}
      >
        <CalendarDays className="size-3.5" />
        {fmtRangeLabel(dateFrom, dateTo)}
      </button>

      {/* Popup */}
      {open && (
        <div className={`absolute left-0 top-full mt-2 z-50 shadow-2xl rounded-xl border flex ${popBg}`}
          style={{ minWidth: 460 }}>

          {/* Sol — Presetler */}
          <div className={`flex flex-col py-3 px-1 border-r min-w-[140px] ${divide}`}>
            {presets.map(p => {
              const active = p.from === dateFrom && p.to === dateTo;
              return (
                <button
                  key={p.label}
                  onClick={() => handlePreset(p.from, p.to)}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs transition-colors ${
                    active ? presetActive : `${presetIdle} ${hov}`
                  }`}
                >
                  {p.label}
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-[#4A9FE8] ml-2 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Sağ — Takvim + butonlar */}
          <div className="flex flex-col p-4 flex-1">
            {/* Navigasyon */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-0.5">
                <button onClick={() => setVy(y => y - 1)} className={`p-1 rounded text-blue-400 hover:text-white transition-colors ${hovBtn}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m11 17-5-5 5-5"/><path d="m18 17-5-5 5-5"/></svg>
                </button>
                <button onClick={prevMonth} className={`p-1 rounded text-blue-400 hover:text-white transition-colors ${hovBtn}`}>
                  <ChevronLeft size={14} />
                </button>
              </div>
              <span className={`text-xs font-semibold ${titleC}`}>
                {MONTHS_TR[vm]} {vy}
              </span>
              <div className="flex gap-0.5">
                <button onClick={nextMonth} className={`p-1 rounded text-blue-400 hover:text-white transition-colors ${hovBtn}`}>
                  <ChevronRight size={14} />
                </button>
                <button onClick={() => setVy(y => y + 1)} className={`p-1 rounded text-blue-400 hover:text-white transition-colors ${hovBtn}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 17 5-5-5-5"/><path d="m13 17 5-5-5-5"/></svg>
                </button>
              </div>
            </div>

            {/* Gün başlıkları */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS_TR.map(d => (
                <div key={d} className="text-center text-[10px] font-medium text-blue-500 py-1">{d}</div>
              ))}
            </div>

            {/* Günler */}
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map((d, i) => {
                if (!d) return <div key={i} />;
                const from  = isFrom(d);
                const to    = isTo(d);
                const range = inRange(d);
                const today = isToday(d);
                return (
                  <button
                    key={d}
                    onClick={() => handleDayClick(d)}
                    className={`
                      relative text-[11px] h-7 w-full flex items-center justify-center rounded transition-colors
                      ${from || to
                        ? 'bg-[#4A9FE8] text-[#061530] font-bold rounded-full z-10'
                        : range
                          ? 'bg-[#4A9FE8]/15 rounded-none ' + (dark ? 'text-white' : 'text-slate-900')
                          : today
                            ? `text-[#4A9FE8] font-semibold ${hovBtn}`
                            : `${dayIdle} ${hov}`
                      }
                    `}
                  >
                    {parseInt(d.split('-')[2])}
                    {today && !from && !to && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#4A9FE8]" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Seçim göstergesi */}
            {(pFrom || pTo) && (
              <div className="mt-2 text-center text-[10px] text-blue-400">
                {pFrom && <span className="text-[#4A9FE8]">{fmtRangeLabel(pFrom, pTo)}</span>}
                {pFrom && !pTo && <span className="text-blue-500"> → bitiş tarihi seçin</span>}
              </div>
            )}

            {/* Butonlar */}
            <div className={`flex gap-2 mt-3 pt-3 border-t ${divide}`}>
              <button
                onClick={() => setOpen(false)}
                className={`flex-1 py-1.5 rounded-lg text-xs border transition-colors ${cancelC} ${hovBtn}`}
              >
                İptal
              </button>
              <button
                onClick={() => { onApply(pFrom, pTo); setOpen(false); }}
                disabled={!pFrom}
                className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-[#4A9FE8] text-[#061530] hover:bg-[#e5a820] disabled:opacity-40 transition-colors"
              >
                Tarihleri Uygula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
